import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import {
    Target,
    Layout,
    BarChart3,
    ArrowLeft,
    BookOpen,
    ListTodo,
    Link2,
    RefreshCw,
    TrendingUp,
    HelpCircle,
    Zap,
    CheckCircle2,
    GitBranch,
    Bot,
    Shield,
    Users,
} from 'lucide-react';
import clsx from 'clsx';
import ThemeToggle from '@/components/ThemeToggle';

// Sidebar categories
const categories = [
    {
        id: 'getting-started',
        icon: Zap,
        labelKey: 'docs.nav.gettingStarted',
    },
    {
        id: 'objectives',
        icon: Target,
        labelKey: 'docs.nav.objectives',
    },
    {
        id: 'key-results',
        icon: TrendingUp,
        labelKey: 'docs.nav.keyResults',
    },
    {
        id: 'boards',
        icon: Layout,
        labelKey: 'docs.nav.boards',
    },
    {
        id: 'tasks',
        icon: ListTodo,
        labelKey: 'docs.nav.tasks',
    },
    {
        id: 'linking',
        icon: Link2,
        labelKey: 'docs.nav.linking',
    },
    {
        id: 'alignment',
        icon: GitBranch,
        labelKey: 'Alignment & Tree',
    },
    {
        id: 'ai-chat',
        icon: Bot,
        labelKey: 'AI Chat (PRO)',
    },
    {
        id: 'checkin',
        icon: RefreshCw,
        labelKey: 'docs.nav.checkin',
    },
    {
        id: 'analytics',
        icon: BarChart3,
        labelKey: 'docs.nav.analytics',
    },
    {
        id: 'team',
        icon: Users,
        labelKey: 'Team & Bảo mật',
    },
    {
        id: 'faq',
        icon: HelpCircle,
        labelKey: 'docs.nav.faq',
    },
];

export default function DocsPage() {
    const { t } = useTranslation();
    const [activeSection, setActiveSection] = useState('getting-started');

    return (
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex">
            {/* Sidebar */}
            <aside className="w-64 bg-white dark:bg-neutral-800 border-r border-neutral-200 dark:border-neutral-700 sticky top-0 h-screen overflow-y-auto hidden md:block">
                <div className="p-4 border-b border-neutral-200 dark:border-neutral-700">
                    <Link to="/" className="flex items-center gap-2 text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white mb-4">
                        <ArrowLeft className="w-4 h-4" />
                        <span className="text-sm">{t('common.back')}</span>
                    </Link>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <BookOpen className="w-5 h-5 text-primary-600" />
                            <h1 className="text-lg font-bold">{t('docs.title')}</h1>
                        </div>
                        <ThemeToggle />
                    </div>
                </div>

                <nav className="p-2">
                    {categories.map((cat) => (
                        <button
                            key={cat.id}
                            onClick={() => setActiveSection(cat.id)}
                            className={clsx(
                                'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left text-sm transition-colors',
                                activeSection === cat.id
                                    ? 'bg-primary-50 dark:bg-primary-600/20 text-primary-700 dark:text-primary-300 font-medium'
                                    : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700'
                            )}
                        >
                            <cat.icon className="w-4 h-4" />
                            {t(cat.labelKey)}
                        </button>
                    ))}
                </nav>
            </aside>

            {/* Mobile header */}
            <div className="md:hidden fixed top-0 left-0 right-0 bg-white dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700 z-10 p-4">
                <div className="flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <ArrowLeft className="w-5 h-5" />
                    </Link>
                    <div className="flex items-center gap-2">
                        <BookOpen className="w-5 h-5 text-primary-600" />
                        <h1 className="font-bold">{t('docs.title')}</h1>
                    </div>
                    <div className="w-9" />
                </div>
                {/* Mobile category selector */}
                <select
                    value={activeSection}
                    onChange={(e) => setActiveSection(e.target.value)}
                    className="mt-3 w-full p-2 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700"
                >
                    {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                            {t(cat.labelKey)}
                        </option>
                    ))}
                </select>
            </div>

            {/* Main Content */}
            <main className="flex-1 p-6 md:p-8 mt-28 md:mt-0 overflow-y-auto">
                <div className="max-w-3xl mx-auto">
                    {activeSection === 'getting-started' && <GettingStartedSection />}
                    {activeSection === 'objectives' && <ObjectivesSection />}
                    {activeSection === 'key-results' && <KeyResultsSection />}
                    {activeSection === 'boards' && <BoardsSection />}
                    {activeSection === 'tasks' && <TasksSection />}
                    {activeSection === 'linking' && <LinkingSection />}
                    {activeSection === 'alignment' && <AlignmentSection />}
                    {activeSection === 'ai-chat' && <AIChatSection />}
                    {activeSection === 'checkin' && <CheckinSection />}
                    {activeSection === 'analytics' && <AnalyticsSection />}
                    {activeSection === 'team' && <TeamSection />}
                    {activeSection === 'faq' && <FaqSection />}

                    {/* Footer */}
                    <footer className="mt-12 pt-6 border-t border-neutral-200 dark:border-neutral-700 text-center text-sm text-neutral-500">
                        <p>VisionBoard © 2025</p>
                        <p className="mt-1">
                            <Link to="/" className="text-primary-600 hover:underline">Trang chủ</Link>
                            {' • '}
                            <Link to="/login" className="text-primary-600 hover:underline">Đăng nhập</Link>
                        </p>
                    </footer>
                </div>
            </main>
        </div>
    );
}

// ===== Section Components =====

function GettingStartedSection() {
    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-2xl font-bold mb-2">🚀 Bắt đầu nhanh</h2>
                <p className="text-neutral-600 dark:text-neutral-400">
                    VisionBoard giúp bạn quản lý mục tiêu theo phương pháp OKR kết hợp Kanban.
                </p>
            </div>

            <div className="bg-primary-50 dark:bg-primary-600/10 rounded-xl p-6">
                <h3 className="font-semibold mb-4">4 bước để bắt đầu:</h3>
                <div className="grid gap-4">
                    <Step number={1} title="Tạo Mục tiêu (Objective)" desc="Đặt mục tiêu lớn bạn muốn đạt được" />
                    <Step number={2} title="Thêm Key Results" desc="Định nghĩa cách đo lường thành công" />
                    <Step number={3} title="Tạo Board & Tasks" desc="Chia nhỏ thành công việc hàng ngày" />
                    <Step number={4} title="Liên kết & Theo dõi" desc="Gắn task với KR, cập nhật tiến độ" />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <InfoCard icon={Target} title="Objective" desc="Mục tiêu định tính" color="success" />
                <InfoCard icon={TrendingUp} title="Key Result" desc="Kết quả đo lường được" color="primary" />
                <InfoCard icon={Layout} title="Board" desc="Bảng Kanban" color="secondary" />
                <InfoCard icon={ListTodo} title="Task" desc="Công việc cụ thể" color="warning" />
            </div>
        </div>
    );
}

function ObjectivesSection() {
    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-2xl font-bold mb-2">🎯 Mục tiêu (Objectives)</h2>
                <p className="text-neutral-600 dark:text-neutral-400">
                    Objective là mục tiêu lớn bạn muốn đạt được, thường là định tính và truyền cảm hứng.
                </p>
            </div>

            <FieldsTable
                title="Các trường cần điền"
                fields={[
                    { name: 'Tiêu đề', required: true, desc: 'Tên mục tiêu', example: '"Tăng doanh thu Q4"' },
                    { name: 'Mô tả', required: false, desc: 'Chi tiết về mục tiêu', example: '"Tăng 30% so với Q3..."' },
                    { name: 'Chu kỳ', required: true, desc: 'Q1/Q2/Q3/Q4/Năm', example: 'Q4' },
                    { name: 'Năm', required: true, desc: 'Năm thực hiện', example: '2025' },
                    { name: 'Ngày bắt đầu', required: true, desc: 'Ngày bắt đầu theo dõi', example: '01/10/2025' },
                    { name: 'Ngày kết thúc', required: true, desc: 'Deadline', example: '31/12/2025' },
                ]}
            />

            <TipsBox
                title="💡 Mẹo viết Objective tốt"
                items={[
                    'Bắt đầu bằng động từ: "Tăng...", "Ra mắt...", "Xây dựng..."',
                    'Truyền cảm hứng nhưng khả thi',
                    'Không nên quá 5 Objectives mỗi quý',
                ]}
            />
        </div>
    );
}

function KeyResultsSection() {
    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-2xl font-bold mb-2">📊 Key Results (KR)</h2>
                <p className="text-neutral-600 dark:text-neutral-400">
                    Key Result là chỉ số đo lường để biết bạn đã đạt mục tiêu chưa. Phải có con số cụ thể!
                </p>
            </div>

            <FieldsTable
                title="Các trường QUAN TRỌNG"
                fields={[
                    { name: 'Tiêu đề', required: true, desc: 'Tên KR với số cụ thể', example: '"Đạt 1000 users"' },
                    { name: 'Loại (Type)', required: true, desc: 'Cách đo lường', example: 'number / percentage' },
                    { name: 'Bắt đầu (Start)', required: true, desc: 'Giá trị hiện tại', example: '0' },
                    { name: 'Mục tiêu (Target)', required: true, desc: 'Giá trị cần đạt', example: '1000' },
                    { name: 'Đơn vị (Unit)', required: true, desc: 'Đơn vị đo', example: 'users / % / VND' },
                ]}
            />

            <div className="bg-blue-50 dark:bg-blue-600/10 rounded-xl p-6">
                <h3 className="font-semibold mb-4">📝 Giải thích chi tiết</h3>

                <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-white dark:bg-neutral-800 p-4 rounded-lg">
                            <h4 className="font-medium text-blue-700 dark:text-blue-300 mb-2">Start (Bắt đầu)</h4>
                            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-2">Điểm xuất phát hiện tại của bạn</p>
                            <code className="text-xs bg-neutral-100 dark:bg-neutral-700 px-2 py-1 rounded">
                                VD: Chưa có user → Start = 0
                            </code>
                        </div>
                        <div className="bg-white dark:bg-neutral-800 p-4 rounded-lg">
                            <h4 className="font-medium text-blue-700 dark:text-blue-300 mb-2">Target (Mục tiêu)</h4>
                            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-2">Đích bạn muốn đạt được</p>
                            <code className="text-xs bg-neutral-100 dark:bg-neutral-700 px-2 py-1 rounded">
                                VD: Muốn 1000 users → Target = 1000
                            </code>
                        </div>
                        <div className="bg-white dark:bg-neutral-800 p-4 rounded-lg">
                            <h4 className="font-medium text-blue-700 dark:text-blue-300 mb-2">Unit (Đơn vị)</h4>
                            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-2">Đơn vị đo lường</p>
                            <code className="text-xs bg-neutral-100 dark:bg-neutral-700 px-2 py-1 rounded">
                                users, %, features, posts, VND
                            </code>
                        </div>
                        <div className="bg-white dark:bg-neutral-800 p-4 rounded-lg">
                            <h4 className="font-medium text-blue-700 dark:text-blue-300 mb-2">Type (Loại)</h4>
                            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-2">Kiểu số liệu</p>
                            <code className="text-xs bg-neutral-100 dark:bg-neutral-700 px-2 py-1 rounded">
                                number | percentage | currency
                            </code>
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-neutral-100 dark:bg-neutral-800 rounded-xl p-6">
                <h3 className="font-semibold mb-3">🧮 Công thức tính tiến độ</h3>
                <code className="block bg-white dark:bg-neutral-700 p-4 rounded-lg text-sm">
                    Tiến độ = (Hiện tại - Start) / (Target - Start)<br />
                    <span className="text-neutral-500">VD: (350 - 0) / (1000 - 0) = 35%</span>
                </code>
            </div>
        </div>
    );
}

function BoardsSection() {
    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-2xl font-bold mb-2">📋 Boards</h2>
                <p className="text-neutral-600 dark:text-neutral-400">
                    Board là bảng Kanban để quản lý công việc theo từng cột trạng thái.
                </p>
            </div>

            <FieldsTable
                title="Các trường cần điền"
                fields={[
                    { name: 'Tên board', required: true, desc: 'Tên bảng công việc', example: '"Dev Sprint Q4"' },
                    { name: 'Mô tả', required: false, desc: 'Mục đích board', example: '"Sprint hiện tại"' },
                    { name: 'Template', required: true, desc: 'Mẫu có sẵn', example: 'Default / Scrum / Blank' },
                ]}
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <TemplateCard
                    name="Default"
                    columns={['To Do', 'In Progress', 'Done']}
                    desc="Phù hợp công việc đơn giản"
                />
                <TemplateCard
                    name="Scrum"
                    columns={['Backlog', 'Sprint', 'In Progress', 'Review', 'Done']}
                    desc="Phát triển phần mềm"
                />
                <TemplateCard
                    name="Blank"
                    columns={['Tự tạo cột']}
                    desc="Tùy chỉnh hoàn toàn"
                />
            </div>
        </div>
    );
}

function TasksSection() {
    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-2xl font-bold mb-2">✅ Tasks</h2>
                <p className="text-neutral-600 dark:text-neutral-400">
                    Task là công việc cụ thể cần làm, nằm trong các cột của Board.
                </p>
            </div>

            <FieldsTable
                title="Các trường cần điền"
                fields={[
                    { name: 'Tiêu đề', required: true, desc: 'Tên task', example: '"Implement login"' },
                    { name: 'Mô tả', required: false, desc: 'Chi tiết', example: '"Tạo UI và API..."' },
                    { name: 'Độ ưu tiên', required: false, desc: 'Mức quan trọng', example: 'Thấp → Khẩn cấp' },
                    { name: 'Ngày đến hạn', required: false, desc: 'Deadline', example: '15/12/2025' },
                ]}
            />

            <div className="flex flex-wrap gap-3">
                <PriorityBadge level="Thấp" color="neutral" />
                <PriorityBadge level="Trung bình" color="primary" />
                <PriorityBadge level="Cao" color="warning" />
                <PriorityBadge level="Khẩn cấp" color="danger" />
            </div>

            <TipsBox
                title="💡 Mẹo"
                items={[
                    'Kéo thả task giữa các cột để cập nhật trạng thái',
                    'Task vào cột "Done" sẽ tự động đánh dấu hoàn thành',
                ]}
            />
        </div>
    );
}

function LinkingSection() {
    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-2xl font-bold mb-2">🔗 Liên kết Task ↔ KR</h2>
                <p className="text-neutral-600 dark:text-neutral-400">
                    Liên kết task với Key Result để biết công việc đóng góp vào mục tiêu nào.
                </p>
            </div>

            <div className="bg-green-50 dark:bg-green-600/10 rounded-xl p-6">
                <h3 className="font-semibold mb-3">Tại sao cần liên kết?</h3>
                <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5" />
                        Biết task nào đóng góp vào KR nào
                    </li>
                    <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5" />
                        Phát hiện task "lạc lõng" không gắn mục tiêu
                    </li>
                    <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5" />
                        Đo được mức độ alignment của team
                    </li>
                </ul>
            </div>

            <FieldsTable
                title="Khi liên kết"
                fields={[
                    { name: 'Key Result', required: true, desc: 'Chọn KR muốn gắn', example: '"Đạt 1000 users"' },
                    { name: 'Trọng số', required: false, desc: 'Mức đóng góp (1-10)', example: '5 = trung bình' },
                ]}
            />
        </div>
    );
}

function CheckinSection() {
    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-2xl font-bold mb-2">🔄 Check-in tiến độ</h2>
                <p className="text-neutral-600 dark:text-neutral-400">
                    Check-in là hành động cập nhật số liệu thực tế cho Key Result.
                </p>
            </div>

            <FieldsTable
                title="Khi Check-in"
                fields={[
                    { name: 'Giá trị mới', required: true, desc: 'Tiến độ hiện tại', example: '350 (đã có 350 users)' },
                    { name: 'Ghi chú', required: false, desc: 'Giải thích', example: '"Chiến dịch Twitter hiệu quả"' },
                ]}
            />

            <div className="bg-yellow-50 dark:bg-yellow-600/10 rounded-xl p-6">
                <h3 className="font-semibold mb-3">📅 Tần suất check-in đề xuất</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><strong>Mục tiêu tuần:</strong> Mỗi ngày</div>
                    <div><strong>Mục tiêu tháng:</strong> 2-3 lần/tuần</div>
                    <div><strong>Mục tiêu quý:</strong> 1-2 lần/tuần</div>
                    <div><strong>Mục tiêu năm:</strong> 1 lần/tuần</div>
                </div>
            </div>
        </div>
    );
}

function AnalyticsSection() {
    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-2xl font-bold mb-2">📈 Phân tích & Báo cáo</h2>
                <p className="text-neutral-600 dark:text-neutral-400">
                    Trang Analytics giúp bạn theo dõi tiến độ và phát hiện rủi ro.
                </p>
            </div>

            <div className="bg-purple-50 dark:bg-purple-600/10 rounded-xl p-6">
                <h3 className="font-semibold mb-4">🎯 Drift Detection (Phát hiện lệch hướng)</h3>
                <div className="space-y-3 text-sm">
                    <div className="bg-white dark:bg-neutral-800 p-3 rounded-lg">
                        <strong>Kỳ vọng</strong> = Số ngày đã qua / Tổng số ngày
                    </div>
                    <div className="bg-white dark:bg-neutral-800 p-3 rounded-lg">
                        <strong>Thực tế</strong> = Trung bình % các Key Results
                    </div>
                    <div className="bg-white dark:bg-neutral-800 p-3 rounded-lg">
                        <strong>Độ lệch</strong> = Kỳ vọng - Thực tế
                    </div>
                </div>

                <div className="mt-4 flex gap-3 text-sm">
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full">🟢 &lt;15%: OK</span>
                    <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full">🟡 15-30%: Cần chú ý</span>
                    <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full">🔴 &gt;30%: Cần hành động</span>
                </div>
            </div>
        </div>
    );
}

function AlignmentSection() {
    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-2xl font-bold mb-2">🌳 Alignment & Alignment Tree</h2>
                <p className="text-neutral-600 dark:text-neutral-400">
                    Tính năng Alignment giúp bạn đảm bảo mọi công việc đều kết nối với mục tiêu.
                </p>
            </div>

            <div className="bg-green-50 dark:bg-green-600/10 rounded-xl p-6">
                <h3 className="font-semibold mb-4">📊 Alignment Score</h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
                    Điểm Alignment đo lường phần trăm task đã được liên kết với Key Result.
                </p>
                <div className="space-y-2 text-sm">
                    <div className="bg-white dark:bg-neutral-800 p-3 rounded-lg">
                        <strong>Công thức:</strong> Alignment Score = (Tasks đã link / Tổng tasks) × 100%
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full">🟢 &gt;80%: Tốt</span>
                        <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full">🟡 50-80%: Cần chú ý</span>
                        <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full">🔴 &lt;50%: Cần cải thiện</span>
                    </div>
                </div>
            </div>

            <div className="bg-blue-50 dark:bg-blue-600/10 rounded-xl p-6">
                <h3 className="font-semibold mb-4">🌲 Alignment Tree</h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
                    Hiển thị trực quan mối quan hệ giữa Objectives → Key Results → Tasks.
                </p>
                <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-blue-600 mt-0.5" />
                        Xem cấu trúc phân cấp OKR của workspace
                    </li>
                    <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-blue-600 mt-0.5" />
                        Phát hiện task "mồ côi" chưa được gắn mục tiêu
                    </li>
                    <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-blue-600 mt-0.5" />
                        Click vào node để liên kết task trực tiếp
                    </li>
                </ul>
            </div>

            <TipsBox
                title="💡 Mẹo tăng Alignment Score"
                items={[
                    'Review tasks hàng tuần để đảm bảo chúng có mục tiêu rõ ràng',
                    'Xóa hoặc archive tasks không còn phù hợp với OKR hiện tại',
                    'Sử dụng Alignment Tree để nhanh chóng link các task mồ côi',
                ]}
            />
        </div>
    );
}

function AIChatSection() {
    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-2xl font-bold mb-2">🤖 AI Chat Assistant</h2>
                <p className="text-neutral-600 dark:text-neutral-400">
                    Sử dụng AI để phân tích OKR, hỏi đáp và nhận gợi ý thông minh.
                </p>
                <span className="inline-block mt-2 px-3 py-1 bg-gradient-to-r from-primary-500 to-secondary-500 text-white text-xs font-bold rounded-full">
                    Tính năng PRO
                </span>
            </div>

            <div className="bg-purple-50 dark:bg-purple-600/10 rounded-xl p-6">
                <h3 className="font-semibold mb-4">✨ Có thể làm gì với AI?</h3>
                <div className="grid gap-3 text-sm">
                    <div className="bg-white dark:bg-neutral-800 p-3 rounded-lg">
                        <strong>📊 Phân tích OKR:</strong> "Phân tích tiến độ OKR Q4 của tôi"
                    </div>
                    <div className="bg-white dark:bg-neutral-800 p-3 rounded-lg">
                        <strong>💡 Gợi ý viết:</strong> "Gợi ý KR cho mục tiêu tăng doanh thu"
                    </div>
                    <div className="bg-white dark:bg-neutral-800 p-3 rounded-lg">
                        <strong>📋 Tạo task:</strong> "Tạo danh sách task cho KR ra mắt sản phẩm"
                    </div>
                    <div className="bg-white dark:bg-neutral-800 p-3 rounded-lg">
                        <strong>🎯 Phân tích rủi ro:</strong> "Những KR nào đang có nguy cơ không đạt?"
                    </div>
                </div>
            </div>

            <div className="bg-amber-50 dark:bg-amber-600/10 rounded-xl p-6 border border-amber-200 dark:border-amber-600/30">
                <h3 className="font-semibold mb-3 text-amber-800 dark:text-amber-300">⚡ Cách sử dụng</h3>
                <ol className="list-decimal list-inside text-sm text-amber-700 dark:text-amber-400 space-y-2">
                    <li>Vào Dashboard hoặc menu → chọn "AI Chat"</li>
                    <li>Nhập câu hỏi hoặc yêu cầu bằng tiếng Việt hoặc tiếng Anh</li>
                    <li>AI sẽ phân tích dữ liệu OKR của bạn và trả lời</li>
                    <li>Lịch sử chat được lưu lại để tham khảo sau</li>
                </ol>
            </div>
        </div>
    );
}

function TeamSection() {
    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-2xl font-bold mb-2">👥 Team & Bảo mật</h2>
                <p className="text-neutral-600 dark:text-neutral-400">
                    Mời thành viên, quản lý quyền và bảo vệ tài khoản của bạn.
                </p>
            </div>

            <div className="bg-blue-50 dark:bg-blue-600/10 rounded-xl p-6">
                <h3 className="font-semibold mb-4">🔗 Mời thành viên</h3>
                <ol className="list-decimal list-inside text-sm space-y-2">
                    <li>Vào Settings → Team Settings</li>
                    <li>Click "Mời thành viên" hoặc "Invite Member"</li>
                    <li>Nhập email và chọn vai trò (Admin/Member/Viewer)</li>
                    <li>Người được mời sẽ nhận email với link tham gia</li>
                </ol>
            </div>

            <FieldsTable
                title="Vai trò (Roles)"
                fields={[
                    { name: 'Owner', required: true, desc: 'Toàn quyền, không thể xóa', example: 'Người tạo workspace' },
                    { name: 'Admin', required: false, desc: 'Quản lý members, OKR, boards', example: 'Team lead' },
                    { name: 'Member', required: false, desc: 'Tạo/sửa tasks, boards của mình', example: 'Team member' },
                    { name: 'Viewer', required: false, desc: 'Chỉ xem, không sửa', example: 'Stakeholder' },
                ]}
            />

            <div className="bg-green-50 dark:bg-green-600/10 rounded-xl p-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-green-600" />
                    Xác thực 2 bước (2FA)
                </h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
                    Bảo vệ tài khoản với xác thực 2 bước sử dụng ứng dụng Authenticator.
                </p>
                <ol className="list-decimal list-inside text-sm space-y-2">
                    <li>Vào Settings → Security → Enable 2FA</li>
                    <li>Quét mã QR bằng Google Authenticator hoặc Authy</li>
                    <li>Nhập mã 6 số để xác nhận</li>
                    <li>Lưu Recovery Codes để backup</li>
                </ol>
            </div>

            <TipsBox
                title="💡 Bảo mật tốt nhất"
                items={[
                    'Bật 2FA cho tất cả tài khoản admin',
                    'Không chia sẻ recovery codes với ai',
                    'Review danh sách members định kỳ',
                ]}
            />
        </div>
    );
}

function FaqSection() {
    const faqs = [
        { q: 'Nên có bao nhiêu KR cho mỗi Objective?', a: 'Nên có 2-5 KRs. Quá nhiều sẽ khó theo dõi.' },
        { q: 'Target nên đặt như nào cho phù hợp?', a: 'Dùng nguyên tắc 70%: Target đạt được 70% thời gian nếu nỗ lực.' },
        { q: 'Task hoàn thành có tự cập nhật KR không?', a: 'Không tự động. Bạn cần check-in thủ công để cập nhật số liệu.' },
        { q: 'Có thể xóa Objective đang có tiến độ không?', a: 'Có, nhưng sẽ mất hết dữ liệu KRs và history.' },
    ];

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-2xl font-bold mb-2">❓ Câu hỏi thường gặp</h2>
            </div>

            <div className="space-y-4">
                {faqs.map((faq, i) => (
                    <div key={i} className="bg-white dark:bg-neutral-800 rounded-xl p-4 border border-neutral-200 dark:border-neutral-700">
                        <h4 className="font-medium mb-2">{faq.q}</h4>
                        <p className="text-sm text-neutral-600 dark:text-neutral-400">{faq.a}</p>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ===== Helper Components =====

function Step({ number, title, desc }: { number: number; title: string; desc: string }) {
    return (
        <div className="flex items-start gap-4">
            <div className="w-8 h-8 rounded-full bg-primary-600 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                {number}
            </div>
            <div>
                <p className="font-medium">{title}</p>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">{desc}</p>
            </div>
        </div>
    );
}

function InfoCard({ icon: Icon, title, desc, color }: { icon: any; title: string; desc: string; color: string }) {
    const colors: Record<string, string> = {
        success: 'bg-success-50 text-success-600 dark:bg-success-600/20',
        primary: 'bg-primary-50 text-primary-600 dark:bg-primary-600/20',
        secondary: 'bg-secondary-50 text-secondary-600 dark:bg-secondary-600/20',
        warning: 'bg-warning-50 text-warning-600 dark:bg-warning-600/20',
    };
    return (
        <div className="bg-white dark:bg-neutral-800 p-4 rounded-xl border border-neutral-200 dark:border-neutral-700">
            <div className={clsx('w-10 h-10 rounded-lg flex items-center justify-center mb-3', colors[color])}>
                <Icon className="w-5 h-5" />
            </div>
            <h4 className="font-medium">{title}</h4>
            <p className="text-sm text-neutral-500">{desc}</p>
        </div>
    );
}

function FieldsTable({ title, fields }: { title: string; fields: { name: string; required: boolean; desc: string; example: string }[] }) {
    return (
        <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 overflow-hidden">
            <div className="px-4 py-3 bg-neutral-50 dark:bg-neutral-700/50 font-medium text-sm">
                {title}
            </div>
            <div className="divide-y divide-neutral-200 dark:divide-neutral-700">
                {fields.map((f, i) => (
                    <div key={i} className="px-4 py-3 flex flex-wrap items-start gap-x-4 gap-y-1 text-sm">
                        <div className="w-32 flex items-center gap-2">
                            <span className="font-medium">{f.name}</span>
                            {f.required && <span className="text-danger-500 text-xs">*</span>}
                        </div>
                        <div className="flex-1 min-w-[200px] text-neutral-600 dark:text-neutral-400">{f.desc}</div>
                        <code className="text-xs bg-neutral-100 dark:bg-neutral-700 px-2 py-1 rounded">{f.example}</code>
                    </div>
                ))}
            </div>
        </div>
    );
}

function TipsBox({ title, items }: { title: string; items: string[] }) {
    return (
        <div className="bg-amber-50 dark:bg-amber-600/10 rounded-xl p-4 border border-amber-200 dark:border-amber-600/30">
            <h4 className="font-medium text-amber-800 dark:text-amber-300 mb-2">{title}</h4>
            <ul className="list-disc list-inside text-sm text-amber-700 dark:text-amber-400 space-y-1">
                {items.map((item, i) => (
                    <li key={i}>{item}</li>
                ))}
            </ul>
        </div>
    );
}

function TemplateCard({ name, columns, desc }: { name: string; columns: string[]; desc: string }) {
    return (
        <div className="bg-white dark:bg-neutral-800 p-4 rounded-xl border border-neutral-200 dark:border-neutral-700">
            <h4 className="font-medium mb-2">{name}</h4>
            <div className="flex flex-wrap gap-1 mb-2">
                {columns.map((col, i) => (
                    <span key={i} className="text-xs bg-neutral-100 dark:bg-neutral-700 px-2 py-0.5 rounded">{col}</span>
                ))}
            </div>
            <p className="text-xs text-neutral-500">{desc}</p>
        </div>
    );
}

function PriorityBadge({ level, color }: { level: string; color: string }) {
    const colors: Record<string, string> = {
        neutral: 'bg-neutral-100 text-neutral-600',
        primary: 'bg-primary-100 text-primary-600',
        warning: 'bg-warning-100 text-warning-600',
        danger: 'bg-danger-100 text-danger-600',
    };
    return <span className={clsx('px-3 py-1 rounded-full text-sm', colors[color])}>{level}</span>;
}
